import { Button } from "@/components/ui/button";
import { CardContent } from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { format, isToday, isYesterday } from "date-fns";
import { motion } from "framer-motion";
import {
  Archive,
  ArchiveRestore,
  CheckCircle,
  InboxIcon,
  MessageCircle,
  Star,
} from "lucide-react";
import React from "react";
import { InlineEntity } from "../InlineEntity";
import { InboxSelectionCheckbox } from "./components/InboxSelectionCheckbox";

// Use the InboxItem type from the API client for full compatibility
import type { InboxItem } from "@cohesive/api-client";

type ActivityType =
  | "pending_confirmation"
  | "chat_started"
  | "chat_message"
  | "tool_used"
  | "error"
  | "system_event"
  | "user_action";

// Helper function to determine activity type from inbox item
const getActivityType = (item: InboxItem): ActivityType => {
  if (item.has_pending_tool_confirmations) {
    return "pending_confirmation";
  }
  if (item.flow_session && item.flow_message?.content) {
    return "chat_message";
  }
  if (item.flow_session && !item.flow_message) {
    return "chat_started";
  }
  return "system_event";
};

// Helper function to extract tool call information
const getToolCallInfo = (item: InboxItem) => {
  const toolCalls = item.flow_message?.metadata?.tool_calls;
  if (!toolCalls || !Array.isArray(toolCalls) || toolCalls.length === 0) {
    return null;
  }

  const toolCall = toolCalls[0]; // Get first tool call
  return {
    platformName:
      toolCall.function?.platform_display_name ||
      toolCall.function?.platform_name,
    platformImage: toolCall.function?.platform_image_url,
    actionDescription: toolCall.function?.arguments?.action_description,
    functionName: toolCall.function?.name,
    arguments: toolCall.function?.arguments,
    confirmationStatus: toolCall.user_confirmation_status,
  };
};

// Helper function to extract key information from item for activity feed style
const getActivityContent = (item: InboxItem, activityType: ActivityType) => {
  const title = item.flow_session?.name || "Agent Activity";
  const toolCallInfo = getToolCallInfo(item);

  let description = "";
  let details = "";
  let actionLabel = "";
  let platformInfo = null;

  switch (activityType) {
    case "pending_confirmation":
      if (toolCallInfo) {
        description = title;
        details = `Agent wants to take action in ${toolCallInfo.platformName || "a platform"}`;
        actionLabel = "Review & Approve";
        platformInfo = {
          name: toolCallInfo.platformName,
          image: toolCallInfo.platformImage,
        };
      } else {
        description = title;
        details = "Your input is needed to continue";
        actionLabel = "Take Action";
      }
      break;
    case "chat_started":
      description = title;
      details = "Started a new conversation with your agent";
      actionLabel = "Open Chat";
      break;
    case "chat_message":
      description = title;
      details =
        item.flow_message?.content?.substring(0, 120) +
          (item.flow_message?.content && item.flow_message.content.length > 120
            ? "..."
            : "") || "Agent sent a message";
      actionLabel = "View Chat";
      break;
    default:
      description = title;
      details = "Agent activity occurred";
      actionLabel = "View Details";
  }

  return {
    title,
    description,
    details,
    actionLabel,
    platformInfo,
    toolCallInfo,
  };
};

// Helper function to format dates
const formatInboxDate = (dateString: string) => {
  const date = new Date(dateString);
  const timeFormat = "h:mm a";

  if (isToday(date)) {
    return format(date, timeFormat);
  } else if (isYesterday(date)) {
    return "Yesterday";
  }
  return format(date, "MMM d");
};

export type InboxCardProps = {
  item: InboxItem;
  onClick?: () => void;
  onStarClick?: (e: React.MouseEvent) => void;
  onArchiveClick?: (e: React.MouseEvent) => void;
  onSelectionChange?: (selected: boolean, item: InboxItem) => void;
  isSelected?: boolean; // For checkbox selection
  isCurrentlyViewed?: boolean; // For indicating the currently viewed item in side pane
  className?: string;
  style?: React.CSSProperties;

  // Activity feed specific props
  completedItems?: Set<string>; // Set of completed item IDs
  recentlyCompletedItems?: Set<string>; // Set of recently completed item IDs
  onDismissCompleted?: (itemId: string) => void; // Callback for dismissing completed items
  showDismissButton?: boolean; // Show dismiss button for completed items
};

/**
 * InboxCard - A unified card component for displaying inbox items
 *
 * Features:
 * - Shows appropriate icons, status indicators, and platform logos
 * - Supports unread indicators, starring, and archiving
 * - Consistent with other EntityCard components
 * - Handles pending confirmations with special badges
 *
 * Usage:
 * ```tsx
 * <InboxCard
 *   item={inboxItem}
 *   onClick={() => handleClick(inboxItem)}
 *   onStarClick={(e) => handleStar(e, inboxItem)}
 *   onArchiveClick={(e) => handleArchive(e, inboxItem)}
 * />
 * ```
 */

export function InboxCard({
  item,
  onClick,
  onStarClick,
  onArchiveClick,
  onSelectionChange,
  isSelected = false,
  isCurrentlyViewed = false,
  className,
  style,
  // Activity feed props
  completedItems,
  recentlyCompletedItems,
  onDismissCompleted,
  showDismissButton = false,
}: InboxCardProps) {
  const [isHovered, setIsHovered] = React.useState(false);
  const [isTouchDevice, setIsTouchDevice] = React.useState(false);
  const activityType = getActivityType(item);

  // Detect touch device on mount
  React.useEffect(() => {
    setIsTouchDevice("ontouchstart" in window || navigator.maxTouchPoints > 0);
  }, []);

  // Get completed states if using activity feed style
  const isCompleted = completedItems?.has(item.id) || false;
  const isRecentlyCompleted = recentlyCompletedItems?.has(item.id) || false;

  // Always use activity feed style content generation
  const activityContent = getActivityContent(item, activityType);
  const displayName = activityContent.description;
  const descriptionText = activityContent.details;
  const platformInfo = activityContent.platformInfo;
  const toolCallInfo = activityContent.toolCallInfo;

  const handleStarClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onStarClick?.(e);
  };

  const handleArchiveClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onArchiveClick?.(e);
  };

  const handleSelectionClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const newSelectedState = !isSelected;
    onSelectionChange?.(newSelectedState, item);
  };

  // Handle touch and click events properly for mobile
  const handleCardTouch = () => {
    // On touch devices, show hover state immediately
    if (isTouchDevice) {
      setIsHovered(true);
    }
  };

  const handleCardClick = (e: React.MouseEvent) => {
    // Ensure we call the onClick handler immediately without waiting
    if (onClick) {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <div className="relative" data-testid="inbox-card">
      <div
        className={cn("w-full rounded-xl touch-manipulation", className, {
          "cursor-pointer": typeof onClick === "function",
          "hover:bg-neutral-100":
            typeof onClick === "function" && !isCurrentlyViewed,
          "opacity-60": item.is_archived || isCompleted,
          "bg-muted/70 dark:bg-muted/50": !item.is_read,
          "bg-neutral-100 dark:bg-neutral-200": isCurrentlyViewed,
        })}
        onClick={handleCardClick}
        onTouchStart={handleCardTouch}
        onMouseEnter={() => !isTouchDevice && setIsHovered(true)}
        onMouseLeave={() => !isTouchDevice && setIsHovered(false)}
        style={{
          ...style,
          // Prevent iOS double-tap zoom on touch devices
          touchAction: "manipulation",
          WebkitTouchCallout: "none",
          WebkitUserSelect: "none",
          userSelect: "none",
        }}
      >
        <CardContent className="p-3">
          <div className="flex items-start gap-3">
            <InboxSelectionCheckbox
              isSelected={isSelected}
              onClick={handleSelectionClick}
              originalIcon={
                <div className="h-10 w-10 shrink-0 flex items-center justify-center">
                  {activityType === "pending_confirmation" ? (
                    <InboxIcon
                      className={cn(
                        "h-5 w-5",
                        "text-neutral-700 dark:text-neutral-300",
                      )}
                    />
                  ) : (
                    <MessageCircle
                      className={cn(
                        "h-5 w-5",
                        item.is_read
                          ? "text-neutral-600 dark:text-neutral-400"
                          : "text-muted-foreground",
                      )}
                    />
                  )}
                </div>
              }
            />

            <div className="flex-1 min-w-0">
              {/* Header Row */}
              <div className="flex items-center justify-between gap-2 mb-1">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  {/* Unread indicator */}
                  {!item.is_read && (
                    <div className="w-2 h-2 rounded-full bg-rust flex-shrink-0" />
                  )}
                  <div className="font-semibold truncate flex items-center gap-2">
                    <span className="align-middle truncate">{displayName}</span>

                    {/* Platform icons after title */}
                    {platformInfo && (
                      <div className="flex items-center gap-1">
                        <div className="flex items-center justify-center">
                          {platformInfo.image && (
                            <img
                              src={platformInfo.image}
                              alt={platformInfo.name}
                              className="w-[16px] h-[16px]"
                            />
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-1 flex-shrink-0">
                  {/* Date/time shown by default, action buttons on hover/touch */}
                  {!(isHovered || isTouchDevice) && (
                    <div className="mr-1 justify-end">
                      <span className="text-xs text-muted-foreground whitespace-nowrap transition-opacity duration-150">
                        {formatInboxDate(item.updated_at)}
                      </span>
                    </div>
                  )}

                  {/* Action buttons shown on hover or always on touch devices */}
                  {(isHovered || isTouchDevice) && (
                    <div className="flex items-center gap-1 transition-opacity duration-150">
                      {onStarClick && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className={cn(
                                  "h-6 w-6 hover:bg-transparent",
                                  item.is_starred
                                    ? "text-gold hover:text-gold"
                                    : "text-muted-foreground hover:text-foreground",
                                )}
                                onClick={handleStarClick}
                              >
                                <Star
                                  className={cn(
                                    "h-4 w-4",
                                    item.is_starred && "fill-current",
                                  )}
                                />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>{item.is_starred ? "Unstar" : "Star"}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}

                      {/* Only show archive button if not waiting for approval */}
                      {onArchiveClick && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 text-muted-foreground hover:text-foreground hover:bg-transparent"
                                onClick={handleArchiveClick}
                              >
                                {item.is_archived ? (
                                  <ArchiveRestore className="h-4 w-4" />
                                ) : (
                                  <Archive className="h-4 w-4" />
                                )}
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>
                                {item.is_archived ? "Unarchive" : "Archive"}
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Content */}
              <div className="space-y-1">
                {/* Description text */}
                {descriptionText && (
                  <p
                    className={cn(
                      "text-sm text-muted-foreground line-clamp-2",
                      {
                        "italic animate-pulse": false,
                      },
                    )}
                  >
                    <InlineEntity>{descriptionText}</InlineEntity>
                  </p>
                )}

                {/* Message content preview */}
                {activityType === "pending_confirmation" &&
                  toolCallInfo?.arguments?.action_description && (
                    <div className="mt-2 p-2 bg-muted/30 rounded border-l-2 border-muted-foreground/20">
                      <div className="text-xs text-muted-foreground/80 font-medium mb-1">
                        Pending action:
                      </div>
                      <p className="text-sm text-foreground/90 line-clamp-2">
                        <InlineEntity>
                          {toolCallInfo.arguments.action_description}
                        </InlineEntity>
                      </p>
                    </div>
                  )}
              </div>

              {/* Action row */}
              {(showDismissButton || (isCompleted && !isRecentlyCompleted)) && (
                <div className="flex items-center justify-between mt-2">
                  {showDismissButton && onDismissCompleted && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDismissCompleted(item.id);
                      }}
                      className="h-7 px-2 text-xs text-muted-foreground"
                    >
                      Dismiss
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>
        </CardContent>

        {/* Recently completed indicator */}
        {isRecentlyCompleted && (
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            className="absolute top-2 right-2 w-6 h-6 bg-lc-active rounded-full flex items-center justify-center shadow-sm"
          >
            <CheckCircle className="w-3 h-3 text-neutral-0" />
          </motion.div>
        )}
      </div>
    </div>
  );
}
